import { useCallback, useEffect, useRef } from "react";
import { ref, set, onDisconnect, serverTimestamp, onValue, off } from "firebase/database";
import { firebaseDb } from "../firebase/app";

/**
 * Hook for managing user presence (online/offline status) in Firebase
 *
 * This hook tracks whether a user is online or offline and updates their
 * presence status in Firebase Realtime Database. It automatically:
 * - Sets the user as online when connected
 * - Sets the user as offline when they disconnect (close browser, lose connection, etc.)
 * - Handles reconnection scenarios
 *
 * Database structure:
 * dealers/{dealerUid}/info/users/{userUid}/presence/
 *   ├── online: true/false
 *   └── lastSeen: timestamp
 *
 * @param dealerUid - The dealer/manager UID (branch UID)
 * @param userUid - The current user's UID (staff UID or manager UID)
 * @param enabled - Whether presence tracking should be enabled (default: true)
 */
export const usePresence = (dealerUid: string | null, userUid: string | null, enabled = true) => {
  const presenceRefPath = useRef<string | null>(null);
  const isInitialized = useRef(false);

  /**
   * Manually set the user as offline
   * Call this on logout
   */
  const setOffline = useCallback(async () => {
    if (!dealerUid || !userUid) return;

    const presencePath = `dealers/${dealerUid}/info/users/${userUid}/presence`;
    const presenceRef = ref(firebaseDb, presencePath);

    try {
      await set(presenceRef, {
        online: false,
        lastSeen: serverTimestamp(),
      });
      console.log(`[Presence] User ${userUid} set to offline`);
    } catch (error: any) {
      // Silently ignore permission errors during logout (auth becomes null)
      const errorMsg = String(error?.message || error || "").toLowerCase();
      if (!errorMsg.includes("permission")) {
        console.error("[Presence] Error setting offline:", error);
      }
    }
  }, [dealerUid, userUid]);

  /**
   * Initialize presence tracking
   */
  useEffect(() => {
    // Skip if not enabled or missing required UIDs
    if (!enabled || !dealerUid || !userUid) {
      return;
    }

    // Prevent double initialization
    const newPresencePath = `dealers/${dealerUid}/info/users/${userUid}/presence`;
    if (isInitialized.current && presenceRefPath.current === newPresencePath) {
      return;
    }

    presenceRefPath.current = newPresencePath;
    isInitialized.current = true;

    const presenceRef = ref(firebaseDb, newPresencePath);
    const connectedRef = ref(firebaseDb, ".info/connected");

    console.log(
      `[Presence] Initializing presence tracking for user ${userUid} under dealer ${dealerUid}`
    );

    // Listen to connection state
    onValue(connectedRef, (snapshot: any) => {
      if (snapshot.val() === true) {
        // We're connected (or reconnected)!
        console.log(`[Presence] Connected to Firebase - setting ${userUid} as online`);

        // Set up what happens when we disconnect
        // This runs on the Firebase server when connection is lost
        onDisconnect(presenceRef)
          .set({
            online: false,
            lastSeen: serverTimestamp(),
          })
          .then(() => {
            // Now set ourselves as online
            set(presenceRef, {
              online: true,
              lastSeen: serverTimestamp(),
            });
          })
          .catch((error: any) => {
            // Silently ignore permission errors during logout (auth becomes null)
            const errorMsg = String(error?.message || error || "").toLowerCase();
            if (!errorMsg.includes("permission")) {
              console.error("[Presence] Error setting up onDisconnect:", error);
            }
          });
      }
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      console.log(`[Presence] Cleaning up presence tracking for user ${userUid}`);
      off(connectedRef);
      isInitialized.current = false;
      presenceRefPath.current = null;
    };
  }, [dealerUid, userUid, enabled]);

  return {
    setOffline,
  };
};

export default usePresence;
